// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */

/* eslint-disable no-restricted-syntax */
import type {
	CustomPerformanceEventFactoryConfig,
	InteractionPerformanceEventFactoryConfig,
	PageLoadMetric,
	PageLoadPerformanceEventFactoryConfig,
	PageSegmentLoadPerformanceEventFactoryConfig,
} from '@atlassian/browser-metrics';
import { browserMetrics, metrics } from '@atlassian/browser-metrics';
import type { Metric } from '@atlassian/browser-metrics/metric';

import { namedRoutes } from '@confluence/named-routes/entry-points/named-routes.ts';

import { createPageSegmentLoadMetric as createGatedPageSegmentLoadMetric } from './createGatedPageSegmentLoadMetric.ts';

export const BM_CONFIGURED_ROUTES: Set<string> = new Set([
	namedRoutes.CREATE_BLOG.name,
	namedRoutes.CREATE_BLOG_ACTION_LEGACY.name,
	namedRoutes.CREATE_PAGE.name,
	namedRoutes.CREATE_PAGE_ACTION_LEGACY.name,
	namedRoutes.EDIT_BLOG.name,
	namedRoutes.EDIT_BLOG_V2.name,
	namedRoutes.EDIT_PAGE.name,
	namedRoutes.EDIT_PAGE_V2.name,
	namedRoutes.EDIT_PAGE_EMBED.name,
	namedRoutes.VIEW_PAGE.name,
	namedRoutes.VIEW_BLOG.name,
	namedRoutes.VIEW_BLOG_DATE_LEGACY.name,
	namedRoutes.SPACE_INDEX.name,
	namedRoutes.SPACE_OVERVIEW.name,
	namedRoutes.TEMPLATE_GALLERY.name,
	namedRoutes.CONTENT_HISTORY.name,
	namedRoutes.ADVANCED_SEARCH.name,
	namedRoutes.HOME.name,
	namedRoutes.SPACE_DIRECTORY.name,
	namedRoutes.SPACE_PAGES.name,
]);

export const isBMPageLoad = (match: { name: string } | null | undefined): boolean => {
	if (!match) {
		return false;
	}
	return BM_CONFIGURED_ROUTES.has(match.name);
};

export const hasBMPageLoadStarted = (): boolean => {
	const currentMetric = browserMetrics.getPageLoadMetric();
	return currentMetric.getData().start !== null;
};

export { getWaterfallTimings } from './getWaterfallTimings.ts';

const pageLoad: (config: PageLoadPerformanceEventFactoryConfig) => PageLoadMetric =
	metrics.pageLoad;
const custom: (config: CustomPerformanceEventFactoryConfig) => Metric = metrics.custom;
const interaction: (config: InteractionPerformanceEventFactoryConfig) => Metric =
	metrics.interaction;
const concurrent: {
	pageSegmentLoad: (
		config: PageSegmentLoadPerformanceEventFactoryConfig,
	) => (concurrentId: string | number) => Metric;
	interaction: (
		config: InteractionPerformanceEventFactoryConfig,
	) => (concurrentId: string | number) => Metric;
	custom: (
		config: CustomPerformanceEventFactoryConfig,
	) => (concurrentId: string | number) => Metric;
} = metrics.concurrent;
const concurrentCustom: (
	config: CustomPerformanceEventFactoryConfig,
) => (concurrentId: string | number) => Metric = concurrent.custom;

export { pageLoad as createPageLoadMetric };
export { interaction as createInteractionMetric };

/**
 * Public page-segment-load factory. Backed by the gated wrapper in
 * `./createGatedPageSegmentLoadMetric.ts`, which routes every method call through the
 * `cc_bm3_deprecation_phase_2` feature gate (gate OFF → BM3 events; gate ON → UFO custom
 * marks/data). Consumers don't need to know about the gate — calling
 * `metric.start()` / `.stop()` / `.cancel()` / `.mark()` / `.markFMP()` /
 * `.startFromPageLoad()` works the same regardless.
 *
 * If you genuinely need the raw, ungated BM3 factory (e.g. parity tests), import
 * `createBM3PageSegmentLoadMetricRaw` from
 * `@confluence/browser-metrics/entry-points/createBM3PageSegmentLoadMetricRaw.ts`.
 *
 * See `next/packages/browser-metrics/AGENTS.md` → Phase 3 for context.
 */
export { createGatedPageSegmentLoadMetric as createPageSegmentLoadMetric };

export { custom as createCustomMetric };
export { concurrentCustom as createConcurrentCustomMetric };
